import React, { useEffect } from 'react';
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    MenuItem,
    Stack,
    TextField,
    Grid,
    FormControl,
    InputLabel,
    Select,
    Checkbox,
    Box,
    Chip
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { Controller, useForm } from 'react-hook-form';
import dayjs from 'dayjs';
import { useQuery } from 'react-query';
import { JobHttpService } from '../../service';

const statusOptions = ['active', 'inactive'];

const EXPERIENCE_LEVELS = [
    { value: 'Expert', label: 'Expert' },
    { value: 'Senior', label: 'Senior' },
    { value: 'Junior', label: 'Junior' },
    { value: 'Regular', label: 'Regular' },
    { value: 'Internship', label: 'Internship' },
    { value: 'Associate', label: 'Associate' }
];

const RECRUITMENT_TIMELINES = [
    '1 to 3 days',
    '3 to 7 days',
    '1 to 2 weeks',
    '2 to 4 weeks',
    'More than 4 weeks'
];

const PAY_RATES = [
    'per hour',
    'per day',
    'per week',
    'per month',
    'per year'
];

const AdminJobEditDialog = ({ open, onClose, job, onSave }) => {
    const {
        register,
        handleSubmit,
        reset,
        control,
        formState: { isSubmitting }
    } = useForm({
        defaultValues: {
            title: '',
            salary: '',
            status: 'active',
            pay_minimum: '',
            pay_maximum: '',
            pay_rate: '',
            address: '',
            location_description: '',
            number_of_hire: '',
            contact_email: '',
            experience_level: '',
            recruitment_timeline: '',
            category: '',
            jobtypes: [],
            schedules: [],
            benefits: [],
            start_date: null,
            end_date: dayjs().add(3, 'month'), // Default to 3 months from now
            description: ''
        }
    });

    // Load dropdown options (same sources as employer JobForm)
    const { data: categoriesData } = useQuery('cms-job-categories', () => JobHttpService.loadCategories());
    const { data: jobTypesData } = useQuery('cms-job-types', () => JobHttpService.loadJobTypes());
    const { data: schedulesData } = useQuery('cms-job-schedules', () => JobHttpService.loadSchedules());
    const { data: benefitsData } = useQuery('cms-job-benefits', () => JobHttpService.loadBenefits());

    const categories = Array.isArray(categoriesData) ? categoriesData : categoriesData?.results ?? [];
    const jobTypes = Array.isArray(jobTypesData) ? jobTypesData : jobTypesData?.results ?? [];
    const schedules = Array.isArray(schedulesData) ? schedulesData : schedulesData?.results ?? [];
    const benefits = Array.isArray(benefitsData) ? benefitsData : benefitsData?.results ?? [];

    useEffect(() => {
        if (job) {
            reset({
                title: job.title ?? '',
                salary: job.salary ?? '',
                status: job.status ?? 'active',
                pay_minimum: job.pay_minimum ?? '',
                pay_maximum: job.pay_maximum ?? '',
                pay_rate: job.pay_rate ?? '',
                address: job.address ?? '',
                location_description: job.location_description ?? '',
                number_of_hire: job.number_of_hire ?? '',
                contact_email: job.contact_email ?? '',
                experience_level: job.experience_level ?? '',
                recruitment_timeline: job.recruitment_timeline ?? '',
                category: job.category?.id ?? '',
                jobtypes: job.jobtypes?.map((jt) => jt.id) ?? [],
                schedules: job.schedules?.map((s) => s.id) ?? [],
                benefits: job.benefits?.map((b) => b.id) ?? [],
                start_date: job.start_date ? dayjs(job.start_date) : null,
                end_date: job.end_date ? dayjs(job.end_date) : dayjs().add(3, 'month'),
                description: job.description ?? ''
            });
        } else if (open) {
            // Reset to defaults when opening for new job
            reset({
                title: '',
                salary: '',
                status: 'active',
                pay_minimum: '',
                pay_maximum: '',
                pay_rate: '',
                address: '',
                location_description: '',
                number_of_hire: '',
                contact_email: '',
                experience_level: '',
                recruitment_timeline: '',
                category: '',
                jobtypes: [],
                schedules: [],
                benefits: [],
                start_date: null,
                end_date: dayjs().add(3, 'month'),
                description: ''
            });
        }
    }, [job, reset, open]);

    const onSubmit = async (values) => {
        const payload = {
            title: values.title,
            description: values.description,
            salary: values.salary,
            status: values.status,
            pay_minimum: values.pay_minimum ? parseFloat(values.pay_minimum) : null,
            pay_maximum: values.pay_maximum ? parseFloat(values.pay_maximum) : null,
            pay_rate: values.pay_rate,
            address: values.address,
            location_description: values.location_description,
            number_of_hire: values.number_of_hire,
            contact_email: values.contact_email,
            experience_level: values.experience_level,
            recruitment_timeline: values.recruitment_timeline,
            start_date: values.start_date ? dayjs(values.start_date).format('YYYY-MM-DD') : null,
            end_date: values.end_date ? dayjs(values.end_date).format('YYYY-MM-DD') : null,
            category: values.category || null,
            jobtypes: values.jobtypes || [],
            schedules: values.schedules || [],
            benefits: values.benefits || []
        };

        await onSave(payload);
        onClose();
    };

    return (
        <Dialog open={open}  onClose={onClose} fullWidth maxWidth="md">
            <DialogTitle>Edit job</DialogTitle>
            <DialogContent dividers>
                <Stack spacing={2} sx={{ mt: 1 }}>
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <TextField
                                label="Job title"
                                fullWidth
                                {...register('title')}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                label="Description"
                                fullWidth
                                multiline
                                minRows={3}
                                {...register('description')}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                label="Status"
                                select
                                fullWidth
                                {...register('status')}
                            >
                                {statusOptions.map((option) => (
                                    <MenuItem key={option} value={option}>
                                        {option.charAt(0).toUpperCase() + option.slice(1)}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth>
                                <InputLabel>Category</InputLabel>
                                <Select
                                    label="Category"
                                    {...register('category')}
                                >
                                    <MenuItem value="">None</MenuItem>
                                    {categories.map((cat) => (
                                        <MenuItem key={cat.id} value={cat.id}>
                                            {cat.title}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                label="Salary (label)"
                                fullWidth
                                {...register('salary')}
                            />
                        </Grid>
                        <Grid item xs={6} sm={3}>
                            <TextField
                                label="Pay minimum"
                                type="number"
                                fullWidth
                                {...register('pay_minimum')}
                            />
                        </Grid>
                        <Grid item xs={6} sm={3}>
                            <TextField
                                label="Pay maximum"
                                type="number"
                                fullWidth
                                {...register('pay_maximum')}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth>
                                <InputLabel>Pay rate</InputLabel>
                                <Select
                                    label="Pay rate"
                                    {...register('pay_rate')}
                                >
                                    <MenuItem value="">None</MenuItem>
                                    {PAY_RATES.map((rate) => (
                                        <MenuItem key={rate} value={rate}>
                                            {rate}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                label="Number of positions"
                                type="number"
                                fullWidth
                                {...register('number_of_hire')}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                label="Contact email"
                                type="email"
                                fullWidth
                                {...register('contact_email')}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                label="Address"
                                fullWidth
                                {...register('address')}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                label="Location description"
                                fullWidth
                                {...register('location_description')}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth>
                                <InputLabel>Experience level</InputLabel>
                                <Select
                                    label="Experience level"
                                    {...register('experience_level')}
                                >
                                    <MenuItem value="">None</MenuItem>
                                    {EXPERIENCE_LEVELS.map((level) => (
                                        <MenuItem key={level.value} value={level.value}>
                                            {level.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth>
                                <InputLabel>Recruitment timeline</InputLabel>
                                <Select
                                    label="Recruitment timeline"
                                    {...register('recruitment_timeline')}
                                >
                                    <MenuItem value="">None</MenuItem>
                                    {RECRUITMENT_TIMELINES.map((timeline) => (
                                        <MenuItem key={timeline} value={timeline}>
                                            {timeline}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                <Controller
                                    name="start_date"
                                    control={control}
                                    render={({ field }) => (
                                        <DatePicker
                                            label="Start date"
                                            value={field.value}
                                            onChange={field.onChange}
                                            slotProps={{ textField: { fullWidth: true } }}
                                        />
                                    )}
                                />
                            </LocalizationProvider>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                <Controller
                                    name="end_date"
                                    control={control}
                                    render={({ field }) => (
                                        <DatePicker
                                            label="End date (Job expiry)"
                                            value={field.value}
                                            onChange={field.onChange}
                                            minDate={dayjs()}
                                            slotProps={{
                                                textField: {
                                                    fullWidth: true,
                                                    helperText: 'Job will be hidden after this date'
                                                }
                                            }}
                                        />
                                    )}
                                />
                            </LocalizationProvider>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth>
                                <InputLabel>Job types</InputLabel>
                                <Controller
                                    name="jobtypes"
                                    control={control}
                                    render={({ field }) => (
                                        <Select
                                            multiple
                                            label="Job types"
                                            value={field.value || []}
                                            onChange={field.onChange}
                                            renderValue={(selected) => (
                                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                                    {selected.map((value) => {
                                                        const jt = jobTypes.find((item) => item.id === value);
                                                        return (
                                                            <Chip
                                                                key={value}
                                                                label={jt?.title || value}
                                                                size="small"
                                                            />
                                                        );
                                                    })}
                                                </Box>
                                            )}
                                        >
                                            {jobTypes.map((jt) => (
                                                <MenuItem key={jt.id} value={jt.id}>
                                                    <Checkbox checked={(field.value || []).indexOf(jt.id) > -1} />
                                                    {jt.title}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    )}
                                />
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth>
                                <InputLabel>Schedules</InputLabel>
                                <Controller
                                    name="schedules"
                                    control={control}
                                    render={({ field }) => (
                                        <Select
                                            multiple
                                            label="Schedules"
                                            value={field.value || []}
                                            onChange={field.onChange}
                                            renderValue={(selected) => (
                                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                                    {selected.map((value) => {
                                                        const schedule = schedules.find((item) => item.id === value);
                                                        return (
                                                            <Chip
                                                                key={value}
                                                                label={schedule?.title || value}
                                                                size="small"
                                                            />
                                                        );
                                                    })}
                                                </Box>
                                            )}
                                        >
                                            {schedules.map((schedule) => (
                                                <MenuItem key={schedule.id} value={schedule.id}>
                                                    <Checkbox checked={(field.value || []).indexOf(schedule.id) > -1} />
                                                    {schedule.title}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    )}
                                />
                            </FormControl>
                        </Grid>
                        <Grid item xs={6}>
                            <FormControl fullWidth>
                                <InputLabel>Benefits</InputLabel>
                                <Controller
                                    name="benefits"
                                    control={control}
                                    render={({ field }) => (
                                        <Select
                                            multiple
                                            label="Benefits"
                                            value={field.value || []}
                                            onChange={field.onChange}
                                            renderValue={(selected) => (
                                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                                    {selected.map((value) => {
                                                        const benefit = benefits.find((item) => item.id === value);
                                                        return (
                                                            <Chip
                                                                key={value}
                                                                label={benefit?.title || value}
                                                                size="small"
                                                            />
                                                        );
                                                    })}
                                                </Box>
                                            )}
                                        >
                                            {benefits.map((benefit) => (
                                                <MenuItem key={benefit.id} value={benefit.id}>
                                                    <Checkbox checked={(field.value || []).indexOf(benefit.id) > -1} />
                                                    {benefit.title}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    )}
                                />
                            </FormControl>
                        </Grid>
                    </Grid>
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} color="inherit">
                    Cancel
                </Button>
                <Button
                    onClick={handleSubmit(onSubmit)}
                    variant="contained"
                    disabled={isSubmitting}
                >
                    {isSubmitting ? 'Saving…' : 'Save changes'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default AdminJobEditDialog;

